import { Link } from 'react-router-dom';
import { ArrowRight, Phone } from 'lucide-react';
import Seo, { orgJsonLd } from '@/components/Seo';
import { Reveal, Parallax, Magnetic } from '@/components/motion';
import { CtaSection } from '@/components/PageHero';
import { CONTACT } from '@/content/site';

const CREDENTIALS = [
  '40+ Years of Business Experience',
  'Six Sigma Black Belt',
  'Founder-led, personal advisory',
];

export default function About() {
  return (
    <>
      <Seo
        title="About Richard Selsor — KTK Advisors | Business Consultant"
        description="Meet Richard Selsor, Founder & CEO of KTK Advisors: over 40 years of business experience, a Six Sigma Black Belt credential, and a practical, disciplined approach to advisory work."
        path="/about"
        jsonLd={orgJsonLd}
      />

      <section className="relative overflow-hidden bg-deep" data-testid="about-hero">
        <div className="aura aura--gold" style={{ width: 520, height: 520, top: -160, right: -120 }} />
        <div className="wrap grid lg:grid-cols-[3fr_2fr] gap-16 items-center pt-40 pb-24 lg:pt-48 lg:pb-28 relative">
          <div>
            <Reveal>
              <p className="eyebrow-mono">About KTK Advisors</p>
              <h1 className="h1 mt-7 max-w-[18ch]">
                Practical guidance from an <span className="gold-italic">experienced operator.</span>
              </h1>
              <p className="lede mt-8 max-w-[54ch]">
                KTK Advisors is the advisory practice of {CONTACT.founder}—
                founder, consultant, and Six Sigma Black Belt with over 40 years
                of business experience.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 flex flex-col gap-3 max-w-md">
                {CREDENTIALS.map((c) => (
                  <div
                    key={c}
                    className="glass flex items-center gap-4 px-6 py-4"
                  >
                    <span className="cap-item !p-0"><span className="dot" /></span>
                    <span className="text-[15px] font-semibold text-ivory">{c}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 flex flex-wrap items-center gap-7">
                <Magnetic>
                  <Link to="/contact" data-testid="about-schedule-consultation-button" className="btn btn-gold">
                    Schedule a Consultation
                    <ArrowRight size={17} />
                  </Link>
                </Magnetic>
                <a
                  href={CONTACT.phoneHref}
                  data-testid="about-phone-link"
                  className="inline-flex items-center gap-2 text-[15px] font-semibold text-ivory/85 hover:text-goldbright transition-colors"
                >
                  <Phone size={15} />
                  {CONTACT.phone}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="relative">
            <Parallax amount={16}>
              <div
                className="tick-frame rounded-2xl overflow-hidden glow-gold"
                style={{ border: '1px solid rgba(178,139,67,0.35)' }}
                data-testid="about-portrait"
              >
                <span className="tick tick--tl" /><span className="tick tick--tr" />
                <span className="tick tick--bl" /><span className="tick tick--br" />
                <img
                  src="/images/founder-portrait.jpg"
                  alt={`${CONTACT.founder}, Founder and CEO of KTK Advisors`}
                  width="1114"
                  height="1412"
                  fetchPriority="high"
                />
              </div>
            </Parallax>
          </div>
        </div>
      </section>

      <section className="bg-deep section" data-testid="about-bio">
        <div className="wrap grid lg:grid-cols-[1fr_2fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">In His Own Words</p>
          </Reveal>
          <div>
            <Reveal delay={0.08}>
              <div className="measure space-y-7 body-copy text-[17px] text-ivory/85">
                <p className="lede !text-ivory">
                  With over 40 years of business experience and a Six Sigma Black
                  Belt credential, I bring a practical, disciplined approach to
                  helping businesses reduce expenses, grow revenue, and improve
                  the service they deliver.
                </p>
                <p>
                  Throughout my career, I have focused on strengthening financial
                  performance while maintaining the quality customers expect. My
                  approach combines process improvement with practical business
                  strategies to identify inefficiencies, uncover overlooked
                  savings, and help business owners make informed decisions about
                  their operations.
                </p>
                <p>
                  Every business is different. That's why I start by understanding
                  your goals, challenges, and how your organization works. Whether
                  you run a small business, lead a growing company, or oversee a
                  large organization, my focus is on finding opportunities that
                  make sense for your business and support its long-term success.
                </p>
                <p>
                  For qualifying cost-reduction engagements, my compensation is
                  tied to the savings achieved. This keeps the focus on measurable
                  financial value, with the terms of each engagement explained
                  upfront.
                </p>
                <p>
                  My goal is to help you retain more of what your business earns,
                  operate more efficiently, and create room to invest in what
                  comes next.
                </p>
              </div>
              <p className="font-display italic text-[32px] text-goldbright mt-12">{CONTACT.founder}</p>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.26em] text-mist mt-2">
                {CONTACT.founderTitle}, KTK Advisors
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-gold/15 section" data-testid="about-values">
        <div className="wrap">
          <Reveal>
            <h2 className="h2 max-w-[22ch]">What working together looks like.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5 mt-14">
            {[
              {
                t: 'Understand first',
                d: 'Every engagement starts with your goals, your challenges, and how your organization actually works—before any recommendation.',
              },
              {
                t: 'Practical over theoretical',
                d: 'Process discipline finds real inefficiencies—not ideas that sound good in a deck but never survive contact with your operations.',
              },
              {
                t: 'Clear terms, always',
                d: 'Scope, fees, and expectations are explained before you decide to move forward. No surprises in the middle of an engagement.',
              },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="glass glass-hover p-8 h-full">
                  <span className="cap-item !p-0"><span className="dot" /></span>
                  <h3 className="h3 mt-5">{v.t}</h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-mist">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        title="Let's talk about your business."
        copy="Bring the challenge that's been on your mind. The first conversation is a consultation—clear, practical, and focused on your priorities."
        buttonLabel="Schedule a Consultation"
        buttonTo="/contact"
        testid="about-cta-button"
      />
    </>
  );
}
